import {Injectable} from '@angular/core';
import {ActivePromotionConfig} from '../config/active-promotion.config';

declare var Stimulsoft: any;

/**
 * Stimulsoft 帳票フォントをページ内 1 回だけ登録する (ira-groupnet issue #151 と同対応)。
 *
 * 従来は帳票を使う各画面が msgothic001-003.ttf (各 8.4MB、計 25MB) を同期 XHR で
 * 画面遷移のたびに登録し直していた。帳票テンプレートが参照する 5 名義を等幅・P ゴシック・P 明朝の
 * 3 ファイルの別名として非同期に登録し、セッション中はキャッシュする。
 *
 * プロポーショナル名義 (MS P明朝 等) まで等幅の MS Gothic にすると、カナ・記号が全角幅になって
 * 文字列が長くなり、売買台帳の顧客名 (幅 8cm・MS PMincho 15pt) が切れる。旧 kobe-ac.app は
 * Windows の P 明朝で描画していたため、MS PMincho は実体の P 明朝 (msmincho002) に、
 * その他の P 名義は MS PGothic (msgothic003) に割り当てる。
 */
@Injectable({providedIn: 'root'})
export class StimulsoftFontService {
    /** フォント実体と、それに割り当てる帳票テンプレート上の名義 */
    static readonly FONTS: {file: string, names: string[]}[] = [
        {file: '/assets/stimulsoft/fonts/msgothic001.ttf', names: ['MS Gothic', 'MS Mincho']},
        {file: '/assets/stimulsoft/fonts/msgothic003.ttf', names: ['MS PGothic', 'MS UI Gothic']},
        {file: '/assets/stimulsoft/fonts/msmincho002.ttf', names: ['MS PMincho']},
    ];
    static readonly LOCALIZATION_FILE = '/assets/stimulsoft/localization/ja.xml';
    /** Stimulsoft ライセンスキー。未設定だとビューアが描画されない (kobeac 売買台帳/デザイナー) */
    static readonly LICENSE_KEY =
        '6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHl26ZSMEMFmk3V4cVIW/z4aEPCZyhHy36IUIO31u64kPjupS5' +
        'RJ4Kkjh5zA8Js69gML1a/uLIe1uPsavO7G5SbuLvcEW0DcE58buMSZAh4scMesvQVrMM5AkiDUBomhUQfwCMrOWHM9' +
        '61Nb0khu5OVxh450w7X/qNMLW/Ncr+Djtsa1cbZzEQ4r7VkoHy7uZEPVVX9G3Qqtt/dB0cY62D4SY5Zal1nD/bV66S' +
        '3ok4BE+uUYDR5LQ+Yr1dtr+aM/BJUdeMdY6UGOaMthyAGzcyJ1CfV+9AQNrprnDoMQLK83sxIpn5aTSX/p/LNdTCZa' +
        'gpBGH4fp2hjcWVrAsVgkNVsL9zaA0v83O3CtoSlmJLK7M7IAmtsV3fyy3LRo6PlMmBmsPJMbsfQdIj9krxN3Hx9IEQ' +
        'QpIyo+/vGqtsCpDAFfQAmcv6UfTYw0j3XWdoiRQWNBhOjx1jRZwogSJ0wzdVnqa78HySzEfTFNpyWyiPFl7WBxTNnB' +
        'wAVazg9gu7dVCbvUJJA+BRdINej8KErdRgfy';

    private loading: Promise<void> | null = null;

    /** localization + フォント登録の完了で resolve。何度呼んでも 1 回だけ実行される */
    ensureLoaded(): Promise<void> {
        if (!this.loading) {
            this.loading = this.load().catch((e) => {
                this.loading = null; // 失敗はキャッシュせず次回呼び出しでリトライ
                throw e;
            });
        }
        return this.loading;
    }

    private async load(): Promise<void> {
        const prefix = ActivePromotionConfig.PREFIX;
        Stimulsoft.Base.StiLicense.Key = StimulsoftFontService.LICENSE_KEY;
        Stimulsoft.Base.Localization.StiLocalization.setLocalizationFile(prefix + StimulsoftFontService.LOCALIZATION_FILE);
        StimulsoftFontService.patchPrint();
        const collection = Stimulsoft.Base.StiFontCollection;
        // 逐次登録: 各ファイルの初回リクエストがブラウザキャッシュに載り、残りの別名登録はキャッシュから返る
        for (const font of StimulsoftFontService.FONTS) {
            const url = prefix + font.file;
            for (const name of font.names) {
                if (typeof collection.addOpentypeFontFileAsync === 'function') {
                    await new Promise<void>((resolve) => collection.addOpentypeFontFileAsync(() => resolve(), url, name));
                } else {
                    collection.addOpentypeFontFile(url, name);
                }
            }
        }
    }

    /**
     * StiReport.print (ビューアの「印刷」もこれを呼ぶ) は帳票 HTML を隠し iframe に書き込んだ直後に
     * print() するため、iframe 側で読み込み中の帳票フォント (各 8〜10MB) の文字が描画されず、
     * PC やキャッシュの状態によっては罫線だけの白紙が印刷される。
     * 同じ設定で HTML を出力し、iframe のフォント読み込み完了を待ってから印刷するよう差し替える。
     */
    private static patchPrint(): void {
        const proto = Stimulsoft.Report.StiReport.prototype;
        if (proto.__printWaitsFonts) return;
        proto.__printWaitsFonts = true;
        proto.print = function (pageRange?: any, exportMode?: any) {
            const Export = Stimulsoft.Report.Export;
            const settings = new Export.StiHtmlExportSettings();
            settings.chartType = Export.StiHtmlChartType.Vector;
            settings.useWatermarkMargins = false;
            settings.removeEmptySpaceAtBottom = false;
            settings.exportMode = exportMode != null ? exportMode : Export.StiHtmlExportMode.Table;
            settings.exportBookmarksMode = Export.StiHtmlExportBookmarksMode.ReportOnly;
            if (pageRange) settings.pageRange = pageRange;
            this.exportDocumentAsync((html: string) => StimulsoftFontService.printHtml(html),
                Stimulsoft.Report.StiExportFormat.Html, null, settings);
        };
    }

    private static async printHtml(html: string): Promise<void> {
        let frame = document.getElementById('stiPrintReportFrame') as HTMLIFrameElement;
        if (frame == null) {
            frame = document.createElement('iframe');
            frame.id = frame.name = 'stiPrintReportFrame';
            frame.width = frame.height = '0';
            frame.style.position = 'absolute';
            frame.style.border = 'none';
            document.body.appendChild(frame);
        }
        const win = frame.contentWindow!;
        win.document.open();
        win.document.write(html);
        win.document.close();
        // ブラウザの「背景のグラフィック」設定が OFF でも帳票の塗り (見出し帯など) を印刷する
        const style = win.document.createElement('style');
        style.textContent = '* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }';
        (win.document.head || win.document.documentElement).appendChild(style);
        const fonts = (win.document as any).fonts;
        if (fonts) {
            // 帳票が使うフォントを明示的に読み込ませてから、全フォントの読み込み完了を待つ
            const loads: Promise<any>[] = [];
            for (const font of StimulsoftFontService.FONTS) {
                for (const name of font.names) loads.push(fonts.load(`12px "${name}"`, '印').catch(() => null));
            }
            await Promise.all(loads);
            await fonts.ready;
        }
        // フォント適用後のレイアウト反映を 1 フレーム待つ
        await new Promise((resolve) => win.requestAnimationFrame(() => setTimeout(resolve)));
        win.focus();
        win.print();
    }
}
